import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchUsers4manual, fetchWhatsAppRecipients, addWhatsAppRecipient, removeWhatsAppRecipient } from '../../utils/api';
import { User } from '../../utils/types';


const WhatsAppSender: React.FC = () => {
    const { t } = useTranslation();

    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const loadUsers = async () => {
            setLoading(true);
            try {
                // Fetch all potential users and the already selected recipients in parallel
                const [usersResponse, selectedUsersResponse] = await Promise.all([
                    fetchUsers4manual(1, '', 100, 'name'), // Fetch more users if needed
                    fetchWhatsAppRecipients()
                ]);

                // Filter all potential users to ensure they have phone details
                const filteredUsers = usersResponse.users.filter((user: User) => user.phone && user.phone_country_code);
                setUsers(filteredUsers);

                // The selected users from the DB might not have all details,
                // so we find the full user object from the `filteredUsers` list.
                const fullSelectedUsers = selectedUsersResponse
                    .map(selectedUser => filteredUsers.find((u: User) => u.id === selectedUser.id))
                    .filter((user): user is User => !!user); // Filter out any undefined results

                setSelectedUsers(fullSelectedUsers);

                console.log('Fetched all users and pre-selected recipients.');
            } catch (error) {
                toast.error(t('whatsAppSender.failedToLoadUsers') as string);
                console.error('Failed to fetch users:', error);
            } finally {
                setLoading(false);
            }
        };

        loadUsers();
    }, [t]);

    const handleSelectUser = async (userId: string) => {
        if (!userId) return;

        const userToAdd = users.find(u => u.id === parseInt(userId, 10));
        // Add user only if they exist and are not already in the selected list
        if (userToAdd && !selectedUsers.some(su => su.id === userToAdd.id)) {
            try {
                await addWhatsAppRecipient(userToAdd.id);
                setSelectedUsers(prev => [...prev, userToAdd]);
                toast.success(`${userToAdd.name} added to recipients.`);
            } catch (error) {
                toast.error(`Failed to add ${userToAdd.name}.`);
            }
        }


    };

    const handleRemoveUser = async (userId: number) => {
        const userToRemove = selectedUsers.find(u => u.id === userId);
        if (!userToRemove) return;

        try {
            await removeWhatsAppRecipient(userId);
            setSelectedUsers(prev => prev.filter(user => user.id !== userId));
            toast.info(`${userToRemove.name} removed from recipients.`);
        } catch (error) {
            toast.error(`Failed to remove ${userToRemove.name}.`);
        }
    };

    // Filter out already selected users from the dropdown list
    const availableUsers = users.filter(u => !selectedUsers.some(su => su.id === u.id));

    return (
        <div className="p-4 space-y-4">
            <h2 className="text-xl font-semibold">{t('whatsAppSender.title')}</h2>
            <div>
                <label htmlFor="user-select" className="block text-base md:text-sm font-medium text-gray-700">
                    {t('whatsAppSender.selectUserLabel')}
                </label>
                <select
                    id="user-select"
                    value="" // Always reset to the placeholder
                    onChange={(e) => handleSelectUser(e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-gray-100"
                    disabled={loading}
                >
                    <option value="" disabled>{loading ? t('whatsAppSender.loadingUsers') : t('whatsAppSender.selectUserPlaceholder')}</option>
                    {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                            {user.name} ({user.phone})
                        </option>
                    ))}
                </select>
            </div>
                            <div>Selected Recipients:</div>

            {/* Display selected users as removable pills */}
            <div className="flex flex-wrap gap-2 mt-2 min-h-[2.5rem]">
                
                {selectedUsers.map(user => (
                    <div key={user.id} className="flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-1 rounded-full">
                        <span>{user.name} [ {user.phone} ]</span>
                        <button
                            onClick={() => handleRemoveUser(user.id)}
                            className="ml-2 -mr-1 p-1 flex items-center justify-center text-blue-600 hover:bg-blue-200 rounded-full"
                            aria-label={`Remove ${user.name}`}
                        >&times;</button>
                    </div>
                ))}
            </div>

        </div>
    );
};

export default WhatsAppSender;